import React, { useContext } from 'react'
import { sessionContext, useGetBusiness } from '@marketing-milk/frontend'
import { SurfacePalette } from '../../tokens'
import { useSprint } from 'hooks/api/useSprint'
import {
  AdCreative,
  NarrativeBlock as NarrativeBlockData,
  Permissions,
} from '@marketing-milk/interfaces'
import { NarrativeEditor } from './NarrativeEditor'
import { creativeWizardUrl } from '../../../creatives/creativeUrl'
import { SectionCard } from './SectionCard'
import { CreativeCardList, CreativeGroupEditing } from './CreativeCardList'
import { CreativeReviewHandoffCard } from './CreativeReviewHandoffCard'
import {
  useMoveStrategyAd,
  usePacketStrategy,
  useRegenerateStrategy,
  useUnpinStrategyAd,
  useUpdateJustification,
  useUpdateStrategyGroup,
} from 'hooks/api/useSprintPackets'
import { useRefreshVideoAssets } from './useRefreshVideoAssets'

interface Props {
  palette: SurfacePalette
  packetID: number
  sprintID?: number
  narrative: NarrativeBlockData[]
}

/**
 * Edit-mode Creative section: the section chrome keeps its packet-local
 * `section` anchor (`<packetID>:creative`), while each rendered creative card
 * carries its own `creative` anchor (see `CreativeCardList`). Source edits open in
 * the Creative Wizard; verdicts live on the review surface for both cohorts.
 */
export const CreativeSection = ({ palette, packetID, sprintID, narrative }: Props) => {
  const business = useGetBusiness()
  const { user } = useContext(sessionContext)
  const { sprintCreatives, sprintCreativesLoading, refetchCreatives } = useSprint(sprintID)

  // Internal-only recovery action: re-sync ad videos + creatives from Facebook to
  // repair missing video relations/URLs. Hidden from client/present view; server
  // still enforces business access on the sync endpoints regardless.
  const isStaff = !!user?.permissions.includes(Permissions.AccessAdminApp)
  const { refresh, isRefreshing } = useRefreshVideoAssets(business.id, {
    refetchCreatives,
    showAuthorizerLink: isStaff,
  })

  // Strategy grouping. Every mutation returns the full refreshed strategy, so
  // the hooks just replace the query's data — no local reconciliation.
  const { strategy } = usePacketStrategy(packetID)
  const regenerate = useRegenerateStrategy(packetID)
  const updateGroup = useUpdateStrategyGroup(packetID)
  const moveAd = useMoveStrategyAd(packetID)
  const unpinAd = useUnpinStrategyAd(packetID)
  const updateJustification = useUpdateJustification(packetID)

  const editing: CreativeGroupEditing = {
    groups: strategy?.groups ?? [],
    onSaveGroup: (groupID, changes) => updateGroup.mutate({ groupID, dto: changes }),
    onMove: (creativeID, groupID) => moveAd.mutate({ creativeID, dto: { groupID } }),
    onUnpin: creativeID => unpinAd.mutate({ creativeID }),
    onSaveJustification: async (creativeID, justification) => {
      // The justification lives on the creative, not the packet, so the
      // strategy payload the mutation returns doesn't carry it back.
      try {
        await updateJustification.mutateAsync({ creativeID, justification })
      } finally {
        await refetchCreatives()
      }
    },
    onRegenerate: () => regenerate.mutate(),
    regenerating: regenerate.isLoading || strategy?.status === 'generating',
  }

  // Edit-only: source edits (typos, copy) belong at the creative, not the packet.
  // A card opens the Wizard directly — reviewing an ad happens on the review
  // surface, so there is nothing for a preview step in between to add.
  const onOpenSource = (creative: AdCreative) =>
    window.open(creativeWizardUrl(business.id, creative.id), '_blank', 'noopener')

  return (
    <SectionCard
      palette={palette}
      sectionKey="creative"
      title="Creative"
      commentAnchor={{ type: 'section', id: `${packetID}:creative`, label: 'Creative' }}
    >
      {/* The overall strategy narrative — the section intro. It rides the same
        per-block narrative path as every other prose section; the server forces
        `locked` on it, so an AE edit is safe from the next regeneration without
        the editor needing to know locking exists. */}
      <div style={{ marginBottom: 24 }}>
        <NarrativeEditor
          palette={palette}
          sectionKey="creative-strategy"
          label="Creative strategy"
          packetID={packetID}
          narrative={narrative}
          placeholder="The thinking behind this sprint's creative…"
        />
      </div>
      <CreativeCardList
        palette={palette}
        creatives={sprintCreatives}
        strategy={strategy}
        loading={sprintCreativesLoading}
        noSprint={!sprintID}
        onOpenSource={onOpenSource}
        editing={editing}
      />
      <CreativeReviewHandoffCard palette={palette} businessID={business.id} packetID={packetID} />
      {isStaff && sprintID && (
        <button
          type="button"
          onClick={refresh}
          disabled={isRefreshing}
          style={{
            marginTop: 12,
            border: 'none',
            background: 'transparent',
            color: palette.textMuted,
            fontSize: 12,
            cursor: isRefreshing ? 'default' : 'pointer',
            opacity: isRefreshing ? 0.6 : 1,
            padding: 0,
          }}
        >
          <i className={`fa fa-${isRefreshing ? 'spinner fa-spin' : 'refresh'} mr-2`} />
          {isRefreshing ? 'Refreshing video assets…' : 'Refresh video assets'}
        </button>
      )}
    </SectionCard>
  )
}
